<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-10">

        <v-btn text color="primary" class="tw-mb-4" @click="$router.push({ name: 'AgentTransactions' })">
          <v-icon left>mdi-arrow-left</v-icon>
          Back to Transactions
        </v-btn>

        <div v-if="loading" class="tw-w-full tw-flex tw-justify-center tw-py-20">
          <v-progress-circular :size="56" color="#00AEEF" indeterminate />
        </div>

        <template v-else-if="transaction">
          <!-- Page Header -->
          <header class="page-header tw-mb-6">
            <h2 class="page-title">Transaction Details</h2>
          </header>

          <!-- Transaction Info Card -->
          <div class="detail-card tw-mb-6">
            <h3 class="detail-section-title">Transaction Information</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Trans Date</span>
                <span class="detail-value">{{ transaction.transDate || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Our Ref</span>
                <span class="detail-value">{{ transaction.ourRef || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Insured</span>
                <span class="detail-value">{{ transaction.insured || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Type</span>
                <span class="detail-value">{{ transaction.type || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Policy/Claim No</span>
                <span class="detail-value">{{ transaction.policyOrClaimNo || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Cover From</span>
                <span class="detail-value">{{ transaction.coverFrom || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Cover To</span>
                <span class="detail-value">{{ transaction.coverTo || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Additional fields if present -->
          <div v-if="additionalFields.length > 0" class="detail-card">
            <h3 class="detail-section-title">Additional Details</h3>
            <div class="detail-grid">
              <div
                v-for="item in additionalFields"
                :key="item.key"
                class="detail-item"
              >
                <span class="detail-label">{{ item.label }}</span>
                <span class="detail-value">{{ item.value }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- Not Found -->
        <div v-else class="empty-state tw-py-16">
          <v-icon size="60" color="#CBD5E1">mdi-swap-horizontal</v-icon>
          <p class="empty-title">Transaction not found</p>
          <p class="empty-subtitle">Please go back and select a transaction from the list.</p>
          <v-btn color="primary" outlined class="tw-mt-4" @click="$router.push({ name: 'AgentTransactions' })">
            Back to Transactions
          </v-btn>
        </div>

      </div>
    </div>
  </agent-default>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

const PRIMARY_KEYS = ['transDate', 'ourRef', 'insured', 'type', 'policyOrClaimNo', 'coverFrom', 'coverTo', 'id'];

export default {
  name: 'AgentTransactionDetails',
  components: { AgentDefault },
  data() {
    return {
      loading: true,
      transaction: null,
    };
  },
  computed: {
    transactionId() {
      return this.$route.params.transactionId;
    },
    additionalFields() {
      if (!this.transaction || typeof this.transaction !== 'object') return [];
      const items = [];
      Object.keys(this.transaction).forEach((key) => {
        if (PRIMARY_KEYS.includes(key)) return;
        const val = this.transaction[key];
        if (val == null || val === '') return;
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).replace(/_/g, ' ');
        const num = Number(val);
        let value = String(val);
        if (typeof val === 'object') {
          value = JSON.stringify(val);
        } else if (Number.isFinite(num)) {
          value = this.formatAmount(val);
        }
        items.push({ key, label, value });
      });
      return items;
    },
  },
  watch: {
    transactionId: {
      immediate: true,
      handler() {
        this.loadTransaction();
      },
    },
  },
  mounted() {
    this.loadTransaction();
  },
  methods: {
    formatAmount(val) {
      if (val == null || val === '') return '—';
      const num = Number(val);
      if (!Number.isFinite(num)) return String(val);
      return num.toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    loadTransaction() {
      this.loading = true;
      try {
        const key = this.transactionId ? `agentTransaction_${this.transactionId}` : null;
        const raw = key ? sessionStorage.getItem(key) : null;
        const parsed = raw ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : null;
        this.transaction = parsed || null;
        trackAgentInteraction(this, 'transaction_details_loaded', {
          transaction_id: this.transactionId || 'unknown',
          found: Boolean(this.transaction),
        });
      } finally {
        this.loading = false;
      }
    },
  },
  metaInfo: {
    title: 'Transaction Details',
  },
};
</script>

<style scoped>
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0;
}

.detail-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  padding: 24px;
}

.detail-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #E8EDF2;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1E4276;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 16px 0 6px;
}

.empty-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}
</style>
